import React, { useMemo, useState } from 'react';
import { ArrowLeft, Archive } from 'lucide-react';
import CDMTable from './CDMTable';
import DifferentialTable from './DifferentialTable';
import BrainstormBoard from './BrainstormBoard';
import CasePanel from './CasePanel';
import Timeline from './Timeline';
import {
  deriveSession, getStages, getSnapshots, computeRanking, getSettings, BOARD_TABS,
} from '../utils/deriveSession';

// Read-only view of an archived session, grid, case, and timeline as they
// stood when archived. Derivation runs as the archive owner so nothing is
// hidden (blind-voting state does not apply retrospectively).
export default function ArchiveViewScreen({ archive, onBack }) {
  const [view, setView] = useState('cdm');
  const data = useMemo(() => archive.data || {}, [archive]);
  const ownerKey = data.meta?.facilitatorKey || '__archive__';

  const derived = useMemo(() => deriveSession(data, ownerKey), [data, ownerKey]);
  const stages = useMemo(() => getStages(data), [data]);
  const snapshots = useMemo(() => getSnapshots(data), [data]);
  const probabilityMode = getSettings(data).probabilityMode;
  const currentRanking = useMemo(
    () => computeRanking(derived.cdmRows, probabilityMode),
    [derived.cdmRows, probabilityMode]
  );

  const noop = () => {};
  const tableProps = {
    userName: '', identityKey: '__viewer__', isFacilitator: false,
    revealed: true, readOnly: true,
    addItem: noop, editItem: noop, deleteItem: noop, deleteRow: noop, vote: noop, setProbability: noop,
  };

  // A guided case archives its brainstorm boards too, show them alongside
  // the grid so a reviewed session isn't missing the History/Physical/Labs work.
  const boards = {
    history: derived.historyBoard,
    physical: derived.physicalBoard,
    labs: derived.labsBoard,
  };
  const hasBoardContent = (tab) =>
    Object.values(boards[tab]?.columns || {}).some((col) => col.length > 0);
  const boardTabs = ['history', 'physical', 'labs'].filter(hasBoardContent);

  const viewTabs = [
    ...boardTabs.map((t) => [t, t[0].toUpperCase() + t.slice(1)]),
    ['cdm', 'CDM'],
    ['differential', 'Differential'],
    ['timeline', 'Timeline'],
  ];

  return (
    <div className="min-h-screen bg-page p-2 sm:p-4 md:p-8">
      <div className="max-w-[1900px] mx-auto">
        <div className="bg-surface border border-line rounded-card p-4 sm:p-6 mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-heading sm:text-title font-normal text-ink flex items-center gap-2">
              <Archive className="w-5 h-5 text-accent" />{archive.title}
            </h1>
            <p className="text-meta text-ink-2 mt-1">
              Archived {new Date(archive.archivedAt).toLocaleString()} · read-only
            </p>
          </div>
          <div className="flex gap-3 items-center">
            <div className="bg-page rounded-card p-1 flex flex-wrap">
              {viewTabs.map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 rounded-ctl text-meta font-medium transition ${view === v ? 'bg-surface border border-line text-accent' : 'text-ink-2'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button onClick={onBack} className="flex items-center gap-1.5 text-body text-ink-2 hover:text-ink transition">
              <ArrowLeft className="w-4 h-4" />Library
            </button>
          </div>
        </div>

        {stages.length > 0 && view !== 'timeline' && (
          <CasePanel stages={stages} isFacilitator readOnly onAddStage={noop} onRevealStage={noop} />
        )}

        {BOARD_TABS[view] && (
          <BrainstormBoard
            tab={view}
            board={boards[view]}
            columns={BOARD_TABS[view].columns}
            rowId={BOARD_TABS[view].rowId}
            caseActive={false}
            findings={[]}
            {...tableProps}
            setMatchOverride={noop}
            onAddFinding={noop}
            onRemoveFinding={noop}
          />
        )}
        {view === 'cdm' && (
          <CDMTable rows={derived.cdmRows} probabilityMode={probabilityMode} {...tableProps} />
        )}
        {view === 'differential' && (
          <DifferentialTable rows={derived.diffRows} {...tableProps} />
        )}
        {view === 'timeline' && (
          <Timeline snapshots={snapshots} currentRanking={currentRanking} probabilityMode={probabilityMode} revealed />
        )}
      </div>
    </div>
  );
}
